// 子設備加入（plan/vms-web-v0.md DEV-04；plan/subg-binding-v0.md §4.1）：設備詳細頁「子設備」分頁。
// 貼 QR 的子設備：網頁用攝影機掃 QR（BarcodeDetector + getUserMedia；不支援就手動輸入 EUI + IS 或貼上 QR 內容）→ subg/pair
// → 倒數 180 秒（SmartStart 24 小時）→ 列出新加入的子設備 → 命名、指定位置（企業 home 的場域；個人家戶用地圖擺放）。
// permit_join（限時開放加入）只給不需要 QR 的子設備。station 底下的子攝影機照 ADR-004 的配對，不在這裡。
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../components/ConfirmDialog';
import { Link } from 'react-router-dom';
import { Dialog } from '../../components/Dialog';
import { useScope } from '../../lib/scope';
import { orgApi, type Site } from '../../lib/planeApi';
import { siteRows } from '../../lib/siteTree';
import type { VmsDevice } from '../../lib/devices';
import { locationApi, newChildren, normalizeEui, normalizeIs, parseSubgQr, subgApi, type SubgChild, type SubgWindow } from '../../lib/subg';

type Method = 'scan' | 'manual' | 'permit';

const errCode = (e: unknown) => (e as { code?: string; body?: { error?: string } }).body?.error ?? (e as { code?: string }).code ?? String(e);

/** 子設備分頁：名單 + 「加入子設備」。 */
export function SubgPanel({ dev }: { dev: VmsDevice }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const q = useQuery({ queryKey: ['subg', dev.homeId, dev.aid], queryFn: () => subgApi.status(dev.apiEndpoint, dev.homeId, dev.aid), refetchInterval: 30_000, retry: false });
  if (q.isLoading) return <p className="muted">{t('common.loading')}</p>;
  if (q.error || !q.data) return <p className="error">{t('common.error')}</p>;
  const s = q.data;
  return (
    <div data-subg-panel={dev.aid}>
      <p className="muted">
        {t('subg.panel_hint', { n: s.children.length, max: s.max_children ?? '—' })}
        {s.region ? <> · {t('subg.region', { r: s.region })}</> : null}
      </p>
      {s.can_manage ? (
        <button className="btn btn-primary" onClick={() => setOpen(true)}>
          {t('subg.add')}
        </button>
      ) : (
        <p className="muted small">{t('subg.need_admin')}</p>
      )}
      <ChildTable kids={s.children} dev={dev} canManage={s.can_manage} />
      <SubgJoinDialog open={open} dev={dev} close={() => setOpen(false)} />
    </div>
  );
}

function ChildTable({ kids, dev, canManage }: { kids: SubgChild[]; dev: VmsDevice; canManage: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [removing, setRemoving] = useState<Record<string, string>>({});
  // 移除：閘道 kinio_subg.remove → 子設備下次上行時送 LEAVE；平台收到 kinio_subg.left 才解綁（Eddy 2026-09-27），名單稍後才會少
  const { ask, dialog } = useConfirm();
  const remove = async (c: SubgChild) => {
    const name = c.name || `CH${c.index + 1}`;
    if (!(await ask({ title: t('subg.remove_confirm', { name }), action: t('common.action_remove'), typeToConfirm: name }))) return;
    setRemoving((m) => ({ ...m, [c.device_ref]: 'pending' }));
    try {
      await subgApi.remove(dev.apiEndpoint, dev.homeId, dev.aid, c.index);
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: ['subg', dev.homeId, dev.aid] });
        void qc.invalidateQueries({ queryKey: ['home', dev.homeId] });
      }, 2000);
    } catch (e) {
      setRemoving((m) => ({ ...m, [c.device_ref]: errCode(e) }));
    }
  };
  if (!kids.length) return <p className="muted">{t('subg.none')}</p>;
  return (
    <>
      {dialog}
      <table className="grid">
        <thead>
          <tr>
            <th>{t('subg.col_slot')}</th>
            <th>{t('devices.col_name')}</th>
            <th>EUI</th>
            <th>{t('devices.col_model')}</th>
            <th>{t('subg.col_mode')}</th>
            {canManage && <th />}
          </tr>
        </thead>
        <tbody>
          {kids.map((c) => (
            <tr key={c.device_ref} data-subg-child={c.device_ref}>
              <td>CH{c.index + 1}</td>
              <td>
                <Link to={`/devices/${encodeURIComponent(dev.homeId)}/${encodeURIComponent(c.device_ref)}`}>{c.name || `CH${c.index + 1}`}</Link>
              </td>
              <td className="mono">{c.unique_id ?? '—'}</td>
              <td>{c.model ?? c.type_code ?? '—'}</td>
              <td>{c.join_mode ? t(`subg.mode_${c.join_mode}`) : '—'}</td>
              {canManage && (
                <td>
                  {removing[c.device_ref] === 'pending' ? (
                    <span className="muted small">{t('subg.removing')}</span>
                  ) : (
                    <button className="btn btn-sm btn-danger" onClick={() => void remove(c)}>
                      {t('subg.remove')}
                    </button>
                  )}
                  {removing[c.device_ref] && removing[c.device_ref] !== 'pending' && (
                    <div className="error small">{t(`subg.err_${removing[c.device_ref]}`, { defaultValue: t('subg.err_other', { code: removing[c.device_ref] }) })}</div>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

export function SubgJoinDialog({ open, dev, close }: { open: boolean; dev: VmsDevice; close(): void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [method, setMethod] = useState<Method>('scan');
  const [qrText, setQrText] = useState('');
  const [eui, setEui] = useState('');
  const [is, setIs] = useState('');
  const [smart, setSmart] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [win, setWin] = useState<SubgWindow | null>(null);
  const [before, setBefore] = useState<Set<string>>(new Set());
  const [found, setFound] = useState<SubgChild[]>([]);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!open) {
      setWin(null);
      setErr(null);
      setQrText('');
      setEui('');
      setIs('');
      setSmart(false);
      setFound([]);
      setMethod('scan');
    }
  }, [open]);

  const left = win ? Math.max(0, Math.floor((Date.parse(win.expires_at) - now) / 1000)) : 0;
  const running = !!win && left > 0;

  // 開窗期間每 2 秒看一次閘道底下的子設備：不在開窗前名單裡的就是新加入的（QR 模式另外要 EUI 對得上）
  useEffect(() => {
    if (!win) return;
    const id = setInterval(async () => {
      setNow(Date.now());
      try {
        const s = await subgApi.status(dev.apiEndpoint, dev.homeId, dev.aid);
        const fresh = newChildren(before, s.children, win);
        setFound((cur) => (fresh.length !== cur.length ? fresh : cur));
        if (fresh.length) void qc.invalidateQueries({ queryKey: ['home', dev.homeId] });
      } catch {
        /* 下一輪再試 */
      }
    }, 2000);
    return () => clearInterval(id);
  }, [win, before, dev, qc]);

  const start = useCallback(
    async (body: Parameters<typeof subgApi.pair>[3] | 'permit') => {
      setErr(null);
      setBusy(true);
      try {
        const s = await subgApi.status(dev.apiEndpoint, dev.homeId, dev.aid);
        setBefore(new Set(s.children.map((c) => c.device_ref)));
        const w = body === 'permit' ? await subgApi.permitJoin(dev.apiEndpoint, dev.homeId, dev.aid) : await subgApi.pair(dev.apiEndpoint, dev.homeId, dev.aid, body);
        setFound([]);
        setNow(Date.now());
        setWin(w);
      } catch (e) {
        setErr(errCode(e));
      } finally {
        setBusy(false);
      }
    },
    [dev],
  );

  const fromQr = (text: string) => {
    const r = parseSubgQr(text);
    if (!r.ok) {
      setErr(`qr_${r.error}`);
      return;
    }
    void start({ qr: text.trim() });
  };
  const manual = () => {
    if (qrText.trim()) return fromQr(qrText);
    const e = normalizeEui(eui);
    const k = normalizeIs(is);
    if (!e) return setErr('qr_eui');
    if (!k) return setErr('qr_is');
    void start({ eui: e, is: k, ...(smart ? { smartstart: true } : {}) });
  };
  const stop = async () => {
    try {
      await subgApi.stop(dev.apiEndpoint, dev.homeId, dev.aid);
    } catch {
      /* 已經關了 / 閘道離線：窗口會自己到期 */
    }
    setWin((w) => (w ? { ...w, expires_at: new Date().toISOString() } : w));
  };
  const onClose = () => {
    if (running) void stop();
    void qc.invalidateQueries({ queryKey: ['subg', dev.homeId, dev.aid] });
    void qc.invalidateQueries({ queryKey: ['home', dev.homeId] });
    close();
  };

  return (
    <Dialog open={open} title={t('subg.title')} onClose={onClose}>
      {!win ? (
        <>
          <fieldset className="field" role="radiogroup" aria-label={t('subg.method')}>
            <legend>{t('subg.method')}</legend>
            {(['scan', 'manual', 'permit'] as Method[]).map((m) => (
              <label key={m}>
                <input type="radio" name="subg-method" checked={method === m} onChange={() => (setMethod(m), setErr(null))} /> {t(`subg.method_${m}`)}
              </label>
            ))}
          </fieldset>
          {method === 'scan' && <QrScanner onText={fromQr} busy={busy} />}
          {method === 'manual' && (
            <>
              <label className="field">
                <span>{t('subg.qr_text')}</span>
                <input className="input mono" value={qrText} onChange={(e) => setQrText(e.target.value)} placeholder="KSG:1|e=…|s=…|m=…" aria-label={t('subg.qr_text')} />
              </label>
              <p className="muted small">{t('subg.or_fields')}</p>
              <label className="field">
                <span>EUI</span>
                <input className="input mono" value={eui} onChange={(e) => setEui(e.target.value)} placeholder="70B3D5FFFE0A1B2C" aria-label="EUI" disabled={!!qrText.trim()} />
              </label>
              <label className="field">
                <span>{t('subg.is_code')}</span>
                <input className="input mono" value={is} onChange={(e) => setIs(e.target.value)} aria-label={t('subg.is_code')} autoComplete="off" disabled={!!qrText.trim()} />
                <span className="muted small">{t('subg.is_hint')}</span>
              </label>
              <label>
                <input type="checkbox" checked={smart} onChange={(e) => setSmart(e.target.checked)} disabled={!!qrText.trim()} /> {t('subg.smartstart')}
              </label>
            </>
          )}
          {method === 'permit' && (
            <>
              <p>{t('subg.permit_explain')}</p>
              <p className="muted small">{t('subg.permit_warn')}</p>
            </>
          )}
          {err && <p className="error">{t(`subg.err_${err}`, { defaultValue: t('subg.err_other', { code: err }) })}</p>}
          <div className="dialog-actions">
            <button className="btn" onClick={onClose}>
              {t('common.cancel')}
            </button>
            {method === 'manual' && (
              <button className="btn btn-primary" disabled={busy || (!qrText.trim() && (!eui.trim() || !is.trim()))} onClick={manual}>
                {t('subg.start')}
              </button>
            )}
            {method === 'permit' && (
              <button className="btn btn-primary" disabled={busy} onClick={() => void start('permit')}>
                {t('subg.permit_start')}
              </button>
            )}
          </div>
        </>
      ) : (
        <div data-subg-window={running ? win.mode : 'closed'}>
          {running ? (
            <>
              <p>{win.mode === 'qr' ? t(win.smartstart ? 'subg.wait_qr_smart' : 'subg.wait_qr', { eui: win.eui ?? '' }) : t('subg.wait_permit')}</p>
              <p className="muted" data-subg-countdown={left}>
                {t('subg.left', { m: Math.floor(left / 60), s: String(left % 60).padStart(2, '0') })}
              </p>
            </>
          ) : (
            <p className="muted">{found.length ? t('subg.closed_found', { n: found.length }) : t('subg.closed_none')}</p>
          )}
          {found.map((c) => (
            <NewChild key={c.device_ref} c={c} dev={dev} />
          ))}
          <div className="dialog-actions">
            {running ? (
              <button className="btn" onClick={() => void stop()}>
                {t('subg.stop')}
              </button>
            ) : (
              <button className="btn" onClick={() => (setWin(null), setFound([]))}>
                {t('subg.again')}
              </button>
            )}
            <button className="btn btn-primary" onClick={onClose}>
              {t('common.done')}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

/** 一台新加入的子設備：命名、指定位置。 */
function NewChild({ c, dev }: { c: SubgChild; dev: VmsDevice }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const home = useScope().visible.find((h) => h.home_id === dev.homeId);
  const orgId = home?.org_kind === 'team' && typeof home.org_id === 'string' ? home.org_id : '';
  const [name, setName] = useState(c.name ?? '');
  const [saved, setSaved] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const sites = useQuery({
    queryKey: ['sites', orgId],
    queryFn: () => orgApi.sites({ api: dev.apiEndpoint, org_id: orgId }),
    enabled: !!orgId,
    retry: false,
  });
  const rows = useMemo(() => siteRows((sites.data ?? []) as Site[]).filter((r) => r.site.status === 'active'), [sites.data]);
  const [site, setSite] = useState('');
  const save = async () => {
    setErr(null);
    try {
      await subgApi.rename(dev.apiEndpoint, dev.homeId, c.device_ref, name.trim());
      if (orgId && site) {
        const cur = await locationApi.get(dev.apiEndpoint, dev.homeId, c.device_ref).catch(() => ({ location_version: 0 }));
        await locationApi.put(dev.apiEndpoint, dev.homeId, c.device_ref, site, cur?.location_version ?? 0);
      }
      setSaved(name.trim());
      void qc.invalidateQueries({ queryKey: ['home', dev.homeId] });
    } catch (e) {
      setErr(errCode(e));
    }
  };
  return (
    <div className="card" data-subg-new={c.device_ref}>
      <p>
        <strong>{t('subg.joined', { slot: c.index + 1 })}</strong> <span className="mono small">{c.unique_id ?? ''}</span>{' '}
        {c.join_mode && <span className="badge">{t(`subg.mode_${c.join_mode}`)}</span>} {c.model && <span className="muted small">{c.model}</span>}
      </p>
      <label className="field">
        <span>{t('devices.col_name')}</span>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} aria-label={t('subg.name_for', { slot: c.index + 1 })} maxLength={64} />
      </label>
      {orgId ? (
        <label className="field">
          <span>{t('subg.location')}</span>
          <select className="input" value={site} onChange={(e) => setSite(e.target.value)} aria-label={t('subg.location')}>
            <option value="">{t('subg.location_keep')}</option>
            {rows.map((r) => (
              <option key={r.site.site_id} value={r.site.site_id}>
                {' '.repeat(r.depth * 2)}
                {r.site.name}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <p className="muted small">
          {t('subg.location_personal')} <Link to="/map">{t('subg.to_map')}</Link>
        </p>
      )}
      {err && <p className="error">{t(`subg.err_${err}`, { defaultValue: t('subg.err_other', { code: err }) })}</p>}
      <div className="dialog-actions">
        {saved && <span className="muted small">{t('subg.saved', { name: saved })}</span>}
        <button className="btn btn-sm" disabled={!name.trim()} onClick={() => void save()}>
          {t('common.save')}
        </button>
      </div>
    </div>
  );
}

interface Detector {
  detect(src: HTMLVideoElement): Promise<{ rawValue: string }[]>;
}

/** 攝影機掃 QR：BarcodeDetector + getUserMedia；不支援（Firefox / 舊 Safari / 沒攝影機）就請改手動輸入。 */
function QrScanner({ onText, busy }: { onText(s: string): void; busy: boolean }) {
  const { t } = useTranslation();
  const video = useRef<HTMLVideoElement>(null);
  const [state, setState] = useState<'idle' | 'on' | 'unsupported' | 'denied'>('idle');
  const BD = (globalThis as { BarcodeDetector?: new (o: { formats: string[] }) => Detector }).BarcodeDetector;
  const supported = !!BD && !!navigator.mediaDevices?.getUserMedia;
  const cb = useRef(onText);
  cb.current = onText;

  useEffect(() => {
    if (state !== 'on' || !BD) return;
    let stream: MediaStream | null = null;
    let stop = false;
    let last = '';
    const det = new BD({ formats: ['qr_code'] });
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (stop) return;
        const v = video.current!;
        v.srcObject = stream;
        await v.play();
        while (!stop) {
          const codes = await det.detect(v).catch(() => []);
          const hit = codes.find((c) => c.rawValue && c.rawValue !== last);
          if (hit) {
            last = hit.rawValue; // 同一張 QR 不重複送
            cb.current(hit.rawValue);
          }
          await new Promise((r) => setTimeout(r, 300));
        }
      } catch {
        setState('denied');
      }
    })();
    return () => {
      stop = true;
      stream?.getTracks().forEach((tr) => tr.stop());
    };
  }, [state, BD]);

  if (!supported) return <p className="muted" data-subg-scan="unsupported">{t('subg.scan_unsupported')}</p>;
  if (state === 'denied') return <p className="error">{t('subg.scan_denied')}</p>;
  return (
    <div data-subg-scan={state}>
      {state === 'on' ? (
        <>
          <video ref={video} className="qr-video" muted playsInline style={{ width: '100%', maxWidth: 420, borderRadius: 8, background: '#000' }} />
          <p className="muted small">{busy ? t('common.loading') : t('subg.scan_hint')}</p>
        </>
      ) : (
        <button className="btn" onClick={() => setState('on')}>
          {t('subg.scan_start')}
        </button>
      )}
    </div>
  );
}
